import React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ui, state as STATE } from '../../../../../../interfaces/interfaces';
import useDebugInformation from '../../../../../../hooks/useDebugInformation';
import Button from '@mui/material/Button';
import { getActiveStep } from '../../../../../../utilities/validation';
import { reducer, initialStageState, ACTIONS } from './helpers';
import { useAppSelector } from "@store/hooks";

const name = "Stage2Panel"

type Props = {
    parent: string
    prefix: string | string[]
}

const isAppointmentContactValid = (isPropertyFormValid: boolean, isRelatedFromValid: boolean, isLadnlordFormValid: boolean) => {
    if ((ui.CustomerType === "tenant" && ui.ApprovalNeeded) || ui.CustomerType === "landlord") {
        return isLadnlordFormValid && isPropertyFormValid
    }
    else if (ui.CustomerType === "other") {
        return isRelatedFromValid && isPropertyFormValid
    }
    else return isPropertyFormValid
}

const Stage2Panel: React.FC<Props> = ({ parent, prefix }) => {
    const [state, dispatch] = React.useReducer(reducer, initialStageState);
    const isPropertyFormValid = useAppSelector(({ state }) => state.PropertyContactValid)
    const isRelatedFormValid = useAppSelector(({ state }) => state.RelatedContactValid)
    const isLandlordFormValid = useAppSelector(({ state }) => state.LandlordContactValid)
    const isAddressFormValid = useAppSelector(({ state }) => state.PropertyAddressValid)
    const isStep2_3Clicked = useAppSelector(({ state }) => state.Step2_3Clicked)
    const appointmentDate = useAppSelector(({ ui }) => ui.AppointmentDate)
    const appointmentTime = useAppSelector(({ ui }) => ui.AppointmentTime)
    const steps = React.useMemo(() => Object.values(state), [state])
    const [stage2Complete, setStage2Complete] = React.useState(false)
    const [activeStep, setActiveStep] = React.useState(getActiveStep(steps))

    React.useEffect(() => {
        const isAppointmentDate = (appointmentDate && appointmentTime !== null) ? true : false
        STATE.Step2_1Valid = isAppointmentDate
        dispatch(ACTIONS.SET_APPOINTMENT_DATE(false)) //APPOINTMENT DATE STATE
    }, [stage2Complete, prefix, appointmentDate, appointmentTime])

    React.useEffect(() => {
        const valid = isAppointmentContactValid(isPropertyFormValid, isRelatedFormValid, isLandlordFormValid)
        STATE.Step2_2Valid = valid
        dispatch(ACTIONS.SET_CONTACT_DETAILS(prefix, valid)) //CONTACT DETAILS STATE
    }, [isLandlordFormValid, isPropertyFormValid, isRelatedFormValid, prefix])

    React.useEffect(() => {
        STATE.Step2_3Valid = isAddressFormValid && isStep2_3Clicked
        dispatch(ACTIONS.VERIFY_ADDRESS(isAddressFormValid)) //ADDRESS DETAILS STATE
    }, [isAddressFormValid, isStep2_3Clicked])

    React.useEffect(() => {
        setStage2Complete(STATE.Step2_2Valid && STATE.Step2_3Valid && STATE.Step2_1Valid)
    }, [state, isStep2_3Clicked])

    React.useEffect(() => {
        ui.Stage2Complete = stage2Complete
        setActiveStep(getActiveStep(steps))
    }, [activeStep, stage2Complete, steps])

    const debugName = `${parent}▷[${name}]`

    useDebugInformation(debugName, { state, steps, stage2Complete, appointmentDate, appointmentTime, prefix, })

    return (
        <Box id="left-panel" sx={{ width: 260, p: 1 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step, index) => (
                    < Step key={step.label} >
                        <StepLabel
                            optional={
                                index === steps.length - 1 ? (
                                    <Typography variant="caption">Last step of this stage</Typography>
                                ) : null
                            }
                        >
                            {step.label}
                        </StepLabel>
                        <StepContent>
                            <Typography>{step.description}</Typography>
                            {index === 2 && step.description.toLowerCase().includes("fail") && <Button variant='contained' color='secondary'>FAIL</Button>}
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
            {
                activeStep === steps.length && (
                    <Paper square elevation={0} sx={{ p: 3 }}>
                        <Typography>All steps completed - please continue to next stage!</Typography>
                    </Paper>
                )
            }
        </Box >
    );
}

Stage2Panel.displayName = name;


export default Stage2Panel